import {SampleDetails} from 'constants/SampleConstants';
import {
  getBoxesFromCell,
  getPlatesFromCell,
} from 'notebook/sample-propagation/sample-collection/getGridsFromCell';
import makeEmptySampleDetails from 'notebook/sample-propagation/util/makeEmptySampleDetails';
import {
  makeBoxSampleDetails,
  makePlateSampleDetails,
} from 'notebook/sample-propagation/util/makeSampleDetails';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  StorableLinkSchemaFieldTypedValue,
  StorablesTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import APIUtil from 'util/APIUtil';
import {MakeAllFieldsRequired} from 'util/Types';

/**
 * Converts a table cell into an array of SampleDetails object (or null). A null return is not necessarily
 * an error state; it might simply mean we don't understand the cell sufficiently well to do the
 * conversion, or that the cell is empty or still computing.
 *
 * Callers must determine what to do with nulls. Callers are also responsible for deduplicating the results.
 */

export default function getSampleDetailsFromCell(
  cell: Cell | EvaluatedCell,
  columnType: TableColumnType,
  skipLinkedSamples?: boolean
): Array<SampleDetails> | null {
  let typedValue: TypedValue | null = null;
  if (cell.hasOwnProperty('typedValue')) {
    const sourceCell = cell as Cell;
    typedValue = sourceCell.typedValue ?? null;
  } else {
    const computedCell = cell as EvaluatedCell;
    typedValue = computedCell.value;
  }

  if (typedValue == null) {
    return null;
  }

  return getSampleDetailsForTypedValue(typedValue, cell, columnType, skipLinkedSamples);
}

// SampleDetails, but with exactly one key specified, i.e. allows { "bioentityInfo": x } but not { "bioentityInfo": x, "numberInfo": y }
type SingleSampleDetail = {
  [K in keyof SampleDetails]: MakeAllFieldsRequired<Pick<SampleDetails, K>> & // enforce SampleDetails[K] is required
    Partial<Record<Exclude<keyof SampleDetails, K>, never>>; // enforce SampleDetails[not K] is undefined or not specified
}[keyof SampleDetails];

function makeSampleDetailsFromCell(
  cell: Cell | EvaluatedCell,
  sampleDetail: SingleSampleDetail
): SampleDetails {
  return {
    ...makeEmptySampleDetails(),
    error: cell.error,
    ...sampleDetail,
  };
}

function getSampleDetailsForTypedValue(
  typedValue: TypedValue,
  cell: Cell | EvaluatedCell,
  columnType: TableColumnType,
  skipLinkedSamples?: boolean
): Array<SampleDetails> | null {
  if (typedValue.type === BaseTypedValueType.COMPUTING) {
    return null;
  }

  if (typedValue.type === BaseTypedValueType.ARRAY) {
    const arrayResult = typedValue.rawValue
      .map((value) => getSampleDetailsForTypedValue(value, cell, columnType, skipLinkedSamples))
      .flat()
      .filter((value): value is SampleDetails => value != null);
    return arrayResult.length > 0 ? arrayResult : null;
  }

  if (
    columnType === TableColumnType.EXISTING_BIOENTITY ||
    columnType === TableColumnType.DEDICATED_LOOKUP ||
    columnType === TableColumnType.TAG_VALUE ||
    columnType === TableColumnType.CUSTOM
  ) {
    // There are several different types of cells that can contain a propagatable sample
    if (typedValue.type === BaseTypedValueType.BIOENTITY) {
      return [
        makeSampleDetailsFromCell(cell, {
          bioentityInfo: typedValue.bioentitySummary,
        }),
      ];
    }

    if (typedValue.type === FieldType.BOOLEAN || typedValue.type === BaseTypedValueType.BOOLEAN) {
      const value = formatTypedValue(typedValue);
      return [
        makeSampleDetailsFromCell(cell, {
          booleanInfo: {
            id: value,
            name: value,
            value: typedValue.booleanValue ?? null,
          },
        }),
      ];
    }

    if (typedValue.type === FieldType.TEXT || typedValue.type === BaseTypedValueType.STRING) {
      const formattedValue = formatTypedValue(typedValue);
      const value = typedValue.type === FieldType.TEXT ? typedValue.value : typedValue.rawValue;
      if (value !== '') {
        return [
          makeSampleDetailsFromCell(cell, {
            textInfo: {
              id: formattedValue,
              name: formattedValue,
              value,
            },
          }),
        ];
      }
    }

    if (
      typedValue.type === FieldType.INTEGER ||
      typedValue.type === FieldType.FLOAT ||
      typedValue.type === BaseTypedValueType.NUMBER
    ) {
      const val =
        typedValue.type === BaseTypedValueType.NUMBER
          ? typedValue.rawValue
          : typedValue.value === ''
            ? null
            : Number(typedValue.value);

      // Note: isFloat=true for integer custom columns, because a custom column's typedValue does not
      // distinguish between integers and floats. Even though we create a float SampleDetails here,
      // makeSampleCell() will correctly create an integer typed Cell for integer custom columns from
      // a float SampleDetails.
      const isFloat = typedValue.type === FieldType.FLOAT || typedValue.type === BaseTypedValueType.NUMBER;

      const formattedValue = formatTypedValue(typedValue);
      if (val != null) {
        return [
          makeSampleDetailsFromCell(cell, {
            numberInfo: {
              id: formattedValue,
              name: formattedValue,
              isFloat,
              value: val,
            },
          }),
        ];
      }
    }

    if (typedValue.type === FieldType.BIOENTITY_LINK) {
      if (skipLinkedSamples) {
        return null;
      }
      // targetFiles is required by TypeScript, but sometimes it's actually missing (BNCH-20852)
      // We can do a broader audit (BNCH-21410) to see how many typed values are persisted with the wrong shape
      // We also remove any "Deleted Entities" which may have a null "id"
      const bioentities = (typedValue.targetFiles ?? []).filter(({id}) => id != null);
      if (bioentities.length) {
        return bioentities.map(({id, name}) =>
          makeSampleDetailsFromCell(cell, {
            bioentityInfo: {id: id!, name},
          })
        );
      }
      const value = typedValue.value;
      if (value != null) {
        return [
          makeSampleDetailsFromCell(cell, {
            textInfo: {
              id: formatTypedValue(typedValue),
              name: value,
              value: formatTypedValue(typedValue),
            },
          }),
        ];
      }
      return null;
    }
    // StageRunSamples are used in RequestFulfillment tables
    if (
      typedValue.type === BaseTypedValueType.STAGE_RUN_SAMPLE &&
      typedValue.stageRunSample.fileId != null &&
      typedValue.stageRunSample.batch == null
    ) {
      return [
        makeSampleDetailsFromCell(cell, {
          bioentityInfo: {
            id: typedValue.stageRunSample.fileId,
            name: typedValue.stageRunSample.name,
          },
        }),
      ];
    }
    // This cell has data which can't be retrieved as a sample. E.g., a string cell, or a
    // cell that's been pasted from a column of another type. We return null below.
  }

  if (
    (columnType === TableColumnType.CONTAINERS ||
      columnType === TableColumnType.DEDICATED_LOOKUP ||
      columnType === TableColumnType.TAG_VALUE) &&
    (typedValue.type === BaseTypedValueType.STORABLES || typedValue.type === FieldType.STORABLE_LINK)
  ) {
    // Only return valid containers
    const containers =
      (typedValue as StorablesTypedValue).storableSummaries ??
      (typedValue as StorableLinkSchemaFieldTypedValue).targetStorables;
    const validContainerDetails: Array<SampleDetails> = [];
    for (const container of containers) {
      if (APIUtil.isContainer(container.id)) {
        validContainerDetails.push(
          makeSampleDetailsFromCell(cell, {
            containerInfo: {id: container.id, name: container.name, barcode: container.barcode},
          })
        );
      }
    }
    if (validContainerDetails.length > 0) {
      return validContainerDetails;
    }
  }

  function boxDetailsFromCell(cell: Cell | EvaluatedCell): Array<SampleDetails> {
    return getBoxesFromCell(cell).map((box) => makeBoxSampleDetails(box, cell.error));
  }

  function plateDetailsFromCell(cell: Cell | EvaluatedCell): Array<SampleDetails> {
    return getPlatesFromCell(cell).map((plate) => makePlateSampleDetails(plate, cell.error));
  }

  for (const [gridColumnType, gridDetailsFromCell] of [
    [TableColumnType.BOX, boxDetailsFromCell],
    [TableColumnType.PLATE, plateDetailsFromCell],
  ] as Array<[TableColumnType, (cell: Cell | EvaluatedCell) => Array<SampleDetails>]>) {
    if (
      (columnType === gridColumnType ||
        columnType === TableColumnType.DEDICATED_LOOKUP ||
        columnType === TableColumnType.TAG_VALUE) &&
      (typedValue.type === BaseTypedValueType.STORABLES || typedValue.type === FieldType.STORABLE_LINK)
    ) {
      const sampleDetails = gridDetailsFromCell(cell);
      if (sampleDetails.length > 0) {
        return sampleDetails;
      }
    }
  }

  if (
    columnType === TableColumnType.SAMPLE ||
    columnType === TableColumnType.DEDICATED_LOOKUP ||
    columnType === TableColumnType.TAG_VALUE
  ) {
    if (typedValue.type === BaseTypedValueType.BIOENTITY) {
      return [
        makeSampleDetailsFromCell(cell, {
          bioentityInfo: typedValue.bioentitySummary,
        }),
      ];
    }
    if (typedValue.type === BaseTypedValueType.BATCH) {
      return [
        makeSampleDetailsFromCell(cell, {
          batchInfo: typedValue.batchInfo,
        }),
      ];
    }
  }

  return null;
}
